import React from 'react';
import { Icon, Radio, RadioGroup, theme, Tooltip, Space } from 'components';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { RoundClockSVG } from '@bb/elements-web';

type AdvancedSelectorProps = {
  clickHandler: any;
  advConfSetting: boolean;
  noMoreShowings: boolean;
  disableAutoConfirm: boolean;
  supraLockboxAccessHours?: any;
  access?: string;
  supraLockbox?: {
    serialNumber?: string;
  };
  openLockboxHoursModal?: () => void;
  isOldFirmwareLockbox?: any;
};

const AdvancedSelector: React.FC<AdvancedSelectorProps> = ({
  clickHandler,
  advConfSetting,
  noMoreShowings,
  disableAutoConfirm = false,
  supraLockboxAccessHours,
  access,
  supraLockbox,
  openLockboxHoursModal,
  isOldFirmwareLockbox,
}) => {
  const lockboxAssigned =
    access === 'Supra Lockbox' && supraLockbox?.serialNumber;

  const formatTimeRange = (
    startTime: string | Date,
    endTime: string | Date,
  ) => {
    const start = moment(startTime).format('h:mm a');
    const end = moment(endTime).format('h:mm a');
    return `${start} - ${end}`;
  };

  const btleFormat = supraLockboxAccessHours?.monday2Friday;

  const hasAccessHours = !!supraLockboxAccessHours;

  const renderDayRow = (dayLabel: string, dayData: any) => {
    if (!dayData) return null;
    const startTime = dayData.StartTime ?? dayData.startTime;
    const endTime = dayData.EndTime ?? dayData.endTime;

    return (
      <AccessHoursRow key={dayLabel}>
        <DayLabel>{dayLabel}</DayLabel>
        <TimeRange>{formatTimeRange(startTime, endTime)}</TimeRange>
      </AccessHoursRow>
    );
  };

  const renderAccessHours = () => {
    if (btleFormat) {
      return (
        <>
          {renderDayRow('Mon - Fri', supraLockboxAccessHours.monday2Friday)}
          {renderDayRow('Sat', supraLockboxAccessHours.saturday)}
          {renderDayRow('Sun', supraLockboxAccessHours.sunday)}
        </>
      );
    }
    return [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ].map(day => {
      const dayData =
        supraLockboxAccessHours?.[day] ||
        supraLockboxAccessHours?.[day.toLowerCase()];
      return dayData ? renderDayRow(day.substring(0, 3), dayData) : null;
    });
  };

  return (
    <AdvancedSelectorWrapper>
      <AdvancedSelectorContainer>
        <StyledHeading>
          Custom Selector
          <Tooltip
            id="tooltip"
            title="Partially enable auto-confirmation on appointments for only certain time
      periods."
          >
            <span>
              <Icon width={16} type="antdInfoCircleFilled" />
            </span>
          </Tooltip>
        </StyledHeading>
        <StyledHr />
        <form>
          <RadioGroup
            onChange={clickHandler}
            value={
              advConfSetting ? 'advAutoConfEnabled' : 'advAutoConfDisabled'
            }
          >
            <Space direction="vertical" size="small">
              <Radio
                name="advAutoConf"
                value={'advAutoConfDisabled'}
                type="radio"
                data-cy="adv-auto-conf-disable"
              >
                <StyledLabel>Available / Restricted</StyledLabel>
              </Radio>

              <Radio
                name="advAutoConf"
                value={'advAutoConfEnabled'}
                onChange={clickHandler}
                disabled={disableAutoConfirm || !!noMoreShowings}
                data-cy="adv-auto-conf-enable"
              >
                <Tooltip
                  title={
                    noMoreShowings &&
                    'Auto-confirm is disabled. To select this option, disable Turn Off Showings.'
                  }
                >
                  <StyledLabel>
                    Auto-Confirm{' '}
                    <span>
                      <StyledIcon
                        advConfigSetting={advConfSetting}
                        width={12}
                        type="antdThunderBoltFilled"
                      />
                    </span>
                  </StyledLabel>
                </Tooltip>
              </Radio>
            </Space>
          </RadioGroup>
        </form>
      </AdvancedSelectorContainer>

      <LockboxAccessHoursSection>
        <LockboxAccessHoursHeader>
          <LockboxAccessHoursTitle>
            Lockbox Access Hours
          </LockboxAccessHoursTitle>
          <EditButton
            onClick={openLockboxHoursModal}
            disabled={!lockboxAssigned || isOldFirmwareLockbox}
          >
            <Icon width={16} type="edit" />
          </EditButton>
        </LockboxAccessHoursHeader>
        <StyledHr />
        <LockboxAccessHoursBody>
          {lockboxAssigned && hasAccessHours ? (
            <AccessHoursContainer>{renderAccessHours()}</AccessHoursContainer>
          ) : lockboxAssigned && !hasAccessHours ? (
            <>
              <RoundClockSVG />
              <AccessHoursText>
                Access hours have not
                <br />
                been configured.
              </AccessHoursText>
            </>
          ) : (
            <>
              <RoundClockSVG />
              <AccessHoursText>
                Assign a lockbox to the <br />
                listing to select lockbox <br />
                access hours.
              </AccessHoursText>
            </>
          )}
        </LockboxAccessHoursBody>
      </LockboxAccessHoursSection>
    </AdvancedSelectorWrapper>
  );
};

export default AdvancedSelector;

const AdvancedSelectorWrapper = styled.div`
  font-family: ${theme.fontFamily};
  display: flex;
  flex-direction: column;
  margin-right: 10px;
`;

const AdvancedSelectorContainer = styled.div`
  width: 100%;
  border-radius: 1em;
  background: ${theme.gray2};
  padding: 10px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StyledHeading = styled.p`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  font-weight: 500;
  color: black;
  margin: 0;
`;

const StyledHr = styled.hr`
  margin: 10px 0px !important;
`;

const StyledIcon = styled(Icon)<{ advConfigSetting: any }>`
  svg {
    color: ${({ advConfigSetting }) => advConfigSetting && '#5cb85c'};
    animation: glow 3s ease-in-out infinite alternate;
  }
`;

const StyledLabel = styled.span`
  font-weight: normal;
`;

const LockboxAccessHoursSection = styled.div`
  width: 100%;
  align-items: center;
  margin-top: 15px;
  padding: 10px;
  border-radius: 1em;
  background: ${theme.gray2};
`;

const LockboxAccessHoursHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const LockboxAccessHoursTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  font-style: semibold;
  text-align: center;
`;

const LockboxAccessHoursBody = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 15px;
`;

const AccessHoursText = styled.div`
  color: #6b7280;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
`;

const AccessHoursContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AccessHoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 20px;
`;

const DayLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  min-width: 50px;
  text-align: left;
`;

const TimeRange = styled.div`
  font-size: 16px;
  color: #1f2937;
  font-weight: 400;
`;

const EditButton = styled.button`
  background: none;
  cursor: pointer;
  display: flex;
  justify-content: center;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
